import { useState, useEffect, useCallback } from 'react'

const ACCENT_PRESETS = [
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Emerald', hex: '#10b981' },
  { name: 'Rose', hex: '#f43f5e' },
  { name: 'Violet', hex: '#8b5cf6' },
  { name: 'Cyan', hex: '#06b6d4' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Lime', hex: '#84cc16' },
]

const DENSITY_OPTS = [
  { label: 'Compact', val: 0.85 },
  { label: 'Comfortable', val: 1 },
  { label: 'Spacious', val: 1.15 },
]

function getRelativeLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const [rs, gs, bs] = [r, g, b].map(c =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  )
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

function applyAccent(hex: string) {
  const root = document.documentElement
  root.style.setProperty('--accent', hex)
  root.style.setProperty('--accent-h', hex + 'cc')
  root.style.setProperty('--accent-m', hex + '1a')
  root.style.setProperty('--accent-s', hex + '0d')
  root.style.setProperty('--accent-g', hex + '1f')
  // Auto-detect text contrast
  const lum = getRelativeLuminance(hex)
  root.style.setProperty('--accent-t', lum > 0.4 ? '#09090b' : '#ffffff')
  localStorage.setItem('amber-accent', hex)
}

function applyDensity(val: number) {
  document.documentElement.style.setProperty('--density', String(val))
  localStorage.setItem('amber-density', String(val))
}

/** Call this from root layout on mount to restore saved preferences */
export function restoreCustomizations() {
  const accent = localStorage.getItem('amber-accent')
  const density = localStorage.getItem('amber-density')
  if (accent) applyAccent(accent)
  if (density) document.documentElement.style.setProperty('--density', density)
}

function PaletteIcon({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="13.5" cy="6.5" r=".5" />
      <circle cx="17.5" cy="10.5" r=".5" />
      <circle cx="8.5" cy="7.5" r=".5" />
      <circle cx="6.5" cy="12.5" r=".5" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  )
}

function XIcon({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

interface CustomizerPanelProps {
  open: boolean
  onClose: () => void
}

export function CustomizerPanel({ open, onClose }: CustomizerPanelProps) {
  const [accent, setAccent] = useState(() => localStorage.getItem('amber-accent') || '#f59e0b')
  const [hexInput, setHexInput] = useState(() => localStorage.getItem('amber-accent') || '#f59e0b')
  const [density, setDensity] = useState(() => {
    const saved = localStorage.getItem('amber-density')
    return saved ? parseFloat(saved) : 1
  })

  const isPreset = ACCENT_PRESETS.some(p => p.hex === accent)

  const handleAccent = useCallback((hex: string) => {
    setAccent(hex)
    setHexInput(hex)
    applyAccent(hex)
  }, [])

  const handleDensity = useCallback((val: number) => {
    setDensity(val)
    applyDensity(val)
  }, [])

  // Close on ESC
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(0,0,0,.4)',
        animation: 'in-fade 150ms',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 320,
          background: 'var(--bg-1)', borderLeft: '1px solid var(--border)',
          boxShadow: 'var(--sh-lg)',
          animation: 'in-left 250ms var(--ease)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PaletteIcon size={16} style={{ color: 'var(--accent)' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', fontFamily: 'var(--font)' }}>
              Customize
            </span>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', padding: 4 }}>
            <XIcon size={16} style={{ color: 'var(--text-3)' }} />
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {/* Accent Color */}
          <div style={{ marginBottom: 28 }}>
            <label style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '.06em', color: 'var(--text-3)', marginBottom: 12,
              display: 'block', fontFamily: 'var(--font)',
            }}>
              Brand Color
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {ACCENT_PRESETS.map(p => (
                <div
                  key={p.hex}
                  onClick={() => handleAccent(p.hex)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    padding: '10px 4px', borderRadius: 'var(--r-m)', cursor: 'pointer',
                    border: accent === p.hex ? `2px solid ${p.hex}` : '2px solid transparent',
                    background: accent === p.hex ? `${p.hex}10` : 'transparent',
                    transition: 'all var(--dur-f)',
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: p.hex,
                    boxShadow: accent === p.hex ? `0 0 12px ${p.hex}40` : 'none',
                  }} />
                  <span style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font)' }}>
                    {p.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Custom Color Picker */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginTop: 16,
              padding: '12px 14px', borderRadius: 'var(--r-m)',
              background: !isPreset ? 'var(--accent-m)' : 'transparent',
              border: !isPreset ? '1px solid var(--accent)' : '1px solid var(--border)',
              transition: 'all var(--dur-f)',
            }}>
              <input
                type="color"
                value={accent}
                onChange={e => handleAccent(e.target.value)}
                style={{
                  width: 36, height: 36, borderRadius: 'var(--r-m)',
                  border: '2px solid var(--border-h)',
                  cursor: 'pointer',
                  background: 'var(--bg-2)',
                  padding: 2,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 500, fontFamily: 'var(--font)', marginBottom: 4 }}>
                  Custom Color
                </div>
                <input
                  type="text"
                  value={hexInput}
                  onChange={e => {
                    const v = e.target.value
                    setHexInput(v)
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                      handleAccent(v)
                    }
                  }}
                  placeholder="#f59e0b"
                  maxLength={7}
                  style={{
                    width: 90, height: 28, padding: '0 8px',
                    borderRadius: 'var(--r-s)', border: '1px solid var(--border)',
                    background: 'var(--bg-2)', color: 'var(--text-1)',
                    fontFamily: 'var(--mono)', fontSize: 12,
                    textAlign: 'center', outline: 'none',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Density */}
          <div style={{ marginBottom: 28 }}>
            <label style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '.06em', color: 'var(--text-3)', marginBottom: 12,
              display: 'block', fontFamily: 'var(--font)',
            }}>
              Density
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              {DENSITY_OPTS.map(d => (
                <div
                  key={d.val}
                  onClick={() => handleDensity(d.val)}
                  style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 6, padding: '12px 8px', borderRadius: 'var(--r-m)', cursor: 'pointer',
                    border: density === d.val ? '1px solid var(--accent)' : '1px solid var(--border)',
                    background: density === d.val ? 'var(--accent-m)' : 'transparent',
                    transition: 'all var(--dur-f)',
                  }}
                >
                  <div style={{
                    display: 'flex', gap: d.val === 0.85 ? 1 : d.val === 1 ? 2 : 3,
                  }}>
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{
                        width: d.val === 0.85 ? 3 : d.val === 1 ? 4 : 5,
                        height: d.val === 0.85 ? 3 : d.val === 1 ? 4 : 5,
                        borderRadius: 1,
                        background: density === d.val ? 'var(--accent)' : 'var(--text-3)',
                      }} />
                    ))}
                  </div>
                  <span style={{
                    fontSize: 11,
                    color: density === d.val ? 'var(--accent)' : 'var(--text-2)',
                    fontWeight: density === d.val ? 600 : 400,
                    fontFamily: 'var(--font)',
                  }}>
                    {d.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div style={{
            padding: 16, background: 'var(--bg-2)', borderRadius: 'var(--r-m)',
            border: '1px solid var(--border)',
          }}>
            <div style={{
              fontSize: 12, fontWeight: 500, color: 'var(--text-2)', fontFamily: 'var(--font)',
              marginBottom: 4,
            }}>
              Tenant Branding
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font)', lineHeight: 1.5,
            }}>
              These settings apply to your entire workspace. Team members will see your brand color
              throughout the app. Upload your logo in Settings &rarr; Branding.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Trigger button for the sidebar */
export function CustomizerTrigger({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '0 12px', height: 36, borderRadius: 'var(--r-m)',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: 'var(--text-3)', fontSize: 13, fontWeight: 500, fontFamily: 'var(--font)',
        width: '100%', transition: 'color var(--dur-f)',
      }}
      title="Customize theme"
    >
      <span style={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <PaletteIcon size={18} style={{ color: 'var(--text-3)' }} />
      </span>
      {expanded && <span style={{ whiteSpace: 'nowrap' }}>Customize</span>}
    </button>
  )
}
